import React, { useState, useEffect } from 'react';
import {
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { DragIndicator as DragIndicatorIcon } from '@mui/icons-material';
import { supabase } from '../../config/supabase';
import { ownerService } from '../../services/ownerService';
import { onboardingStepsService } from '../../services/onboardingStepsService';
import { StepContent } from '../../data/onboardingStepsData';

interface ChecklistContentSettingsProps {
  onClose?: () => void;
}

const ChecklistContentSettings: React.FC<ChecklistContentSettingsProps> = ({ onClose }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [orgId, setOrgId] = useState<number | null>(null);
  const [steps, setSteps] = useState<StepContent[]>([]);
  const [originalSteps, setOriginalSteps] = useState<StepContent[]>([]);
  const [draggedStepIndex, setDraggedStepIndex] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    loadSteps();
  }, []);

  const loadSteps = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const currentOrgIdString = await ownerService.getOwnerOrgId();
      if (!currentOrgIdString) {
        setError('Unable to determine organization ID');
        return;
      }
      
      // Convert string to number (org_id is BIGINT in database)
      const currentOrgId = Number(currentOrgIdString);
      if (isNaN(currentOrgId)) {
        setError('Invalid organization ID');
        return;
      }
      
      setOrgId(currentOrgId);

      // Load steps (will return custom if exists, otherwise defaults)
      const loadedSteps = await onboardingStepsService.getOnboardingSteps(currentOrgId);
      setSteps(loadedSteps);
      setOriginalSteps(JSON.parse(JSON.stringify(loadedSteps)));
    } catch (err: any) {
      console.error('Error loading steps:', err);
      setError(err.message || 'Failed to load onboarding steps');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    setConfirmOpen(true);
  };

  const handleConfirmSave = async () => {
    if (!orgId) return;
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('User not authenticated');
        return;
      }

      await onboardingStepsService.saveOnboardingSteps(orgId, steps, user.id);

      setSuccess(true);
      setOriginalSteps(JSON.parse(JSON.stringify(steps)));
      setConfirmOpen(false);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      console.error('Error saving steps:', err);
      setError(err.message || 'Failed to save onboarding steps');
    } finally {
      setSaving(false);
    }
  };
  // Drag-and-drop handlers for steps
  const onStepDragStart = (index: number) => {
    setDraggedStepIndex(index);
  };

  const onStepDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const onStepDrop = (index: number) => {
    if (draggedStepIndex === null || draggedStepIndex === index) {
      setDraggedStepIndex(null);
      return;
    }
    setSteps(prev => {
      const copy = [...prev];
      const [removed] = copy.splice(draggedStepIndex, 1);
      copy.splice(index, 0, removed);
      return copy;
    });
    setDraggedStepIndex(null);
  };

  const handleAddStep = () => {
    const newStep: StepContent = {
      title: 'New Step',
      content: [{ text: '' }],
    };
    setSteps(prev => [...prev, newStep]);
  };

  const handleRemoveStep = (index: number) => {
    const updated = [...steps];
    updated.splice(index, 1);
    setSteps(updated);
  };


  const handleCancel = () => {
    setSteps(JSON.parse(JSON.stringify(originalSteps)));
    setError(null);
    setSuccess(false);
    if (onClose) {
      onClose();
    }
  };

  const handleStepTitleChange = (index: number, newTitle: string) => {
    const updatedSteps = [...steps];
    updatedSteps[index] = { ...updatedSteps[index], title: newTitle };
    setSteps(updatedSteps);
  };

  const handleContentChange = (stepIndex: number, contentIndex: number, field: 'text' | 'link', value: string) => {
    const updatedSteps = [...steps];
    const updatedContent = [...updatedSteps[stepIndex].content];
    updatedContent[contentIndex] = { ...updatedContent[contentIndex], [field]: value };
    updatedSteps[stepIndex] = { ...updatedSteps[stepIndex], content: updatedContent };
    setSteps(updatedSteps);
  };

  const handleAddContentItem = (stepIndex: number) => {
    const updatedSteps = [...steps];
    const updatedContent = [...updatedSteps[stepIndex].content];
    updatedContent.push({ text: '' });
    updatedSteps[stepIndex] = { ...updatedSteps[stepIndex], content: updatedContent };
    setSteps(updatedSteps);
  };

  const handleRemoveContentItem = (stepIndex: number, contentIndex: number) => {
    const updatedSteps = [...steps];
    const updatedContent = [...updatedSteps[stepIndex].content];
    updatedContent.splice(contentIndex, 1);
    updatedSteps[stepIndex] = { ...updatedSteps[stepIndex], content: updatedContent };
    setSteps(updatedSteps);
  };

  const handleToggleLink = (stepIndex: number, contentIndex: number) => {
    const updatedSteps = [...steps];
    const updatedContent = [...updatedSteps[stepIndex].content];
    if (updatedContent[contentIndex].link) {
      const { link, ...rest } = updatedContent[contentIndex];
      updatedContent[contentIndex] = rest;
    } else {
      updatedContent[contentIndex] = { ...updatedContent[contentIndex], link: '' };
    }
    updatedSteps[stepIndex] = { ...updatedSteps[stepIndex], content: updatedContent };
    setSteps(updatedSteps);
  };

  const handleAddSubStep = (stepIndex: number, contentIndex: number) => {
    const updatedSteps = [...steps];
    const updatedContent = [...updatedSteps[stepIndex].content];
    const currentSubSteps = updatedContent[contentIndex].subSteps || [];
    updatedContent[contentIndex] = {
      ...updatedContent[contentIndex],
      subSteps: [...currentSubSteps, ''],
    };
    updatedSteps[stepIndex] = { ...updatedSteps[stepIndex], content: updatedContent };
    setSteps(updatedSteps);
  };

  const handleRemoveSubStep = (stepIndex: number, contentIndex: number, subStepIndex: number) => {
    const updatedSteps = [...steps];
    const updatedContent = [...updatedSteps[stepIndex].content];
    const updatedSubSteps = [...(updatedContent[contentIndex].subSteps || [])];
    updatedSubSteps.splice(subStepIndex, 1);
    if (updatedSubSteps.length === 0) {
      const { subSteps, ...rest } = updatedContent[contentIndex];
      updatedContent[contentIndex] = rest;
    } else {
      updatedContent[contentIndex] = {
        ...updatedContent[contentIndex],
        subSteps: updatedSubSteps,
      };
    }
    updatedSteps[stepIndex] = { ...updatedSteps[stepIndex], content: updatedContent };
    setSteps(updatedSteps);
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        mt: 3,
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
          Checklist Content Settings
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {orgId ? `Organization ID: ${orgId}` : ''}
        </Typography>
      </Box>

      <Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        ) : error && !steps.length ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box>
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Onboarding steps saved successfully!
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Edit your organization's onboarding checklist steps. Changes will be saved to the database.
            </Typography>

            {steps.map((step, stepIndex) => (
              <Accordion
                key={`${stepIndex}-${step.title}`}
                defaultExpanded={stepIndex === 0}
                sx={{ mb: 1 }}
                draggable
                onDragStart={() => onStepDragStart(stepIndex)}
                onDragOver={onStepDragOver}
                onDrop={() => onStepDrop(stepIndex)}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }} onClick={(e) => e.stopPropagation()}>
                    <DragIndicatorIcon sx={{ opacity: 0.6, cursor: 'grab' }} />
                    <Typography sx={{ fontWeight: 700, mr: 1 }}>{stepIndex + 1}.</Typography>
                    <TextField
                      value={step.title}
                      onChange={(e) => handleStepTitleChange(stepIndex, e.target.value)}
                      variant="standard"
                      fullWidth
                      sx={{
                        '& .MuiInputBase-input': {
                          fontWeight: 600,
                          fontSize: '1.1rem',
                        },
                      }}
                    />
                    <Tooltip title="Remove Step">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => { e.stopPropagation(); handleRemoveStep(stepIndex); }}
                        sx={{ ml: 1 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ pl: 2 }}>
                    {step.content.map((contentItem, contentIndex) => (
                      <Paper
                        key={contentIndex}
                        elevation={1}
                        sx={{ p: 2, mb: 2, bgcolor: 'background.default', position: 'relative' }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 600 }}>
                            Content Block {contentIndex + 1}
                          </Typography>
                          <Tooltip title="Remove Content Block">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveContentItem(stepIndex, contentIndex)}
                              sx={{ ml: 1 }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        
                        <TextField
                          label="Text Content"
                          value={contentItem.text}
                          onChange={(e) => handleContentChange(stepIndex, contentIndex, 'text', e.target.value)}
                          fullWidth
                          multiline
                          rows={3}
                          sx={{ mb: 2 }}
                          required
                        />
                        
                        <Box sx={{ mb: 2 }}>
                          {contentItem.link !== undefined ? (
                            <Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  Link URL
                                </Typography>
                                <Tooltip title="Remove Link">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleToggleLink(stepIndex, contentIndex)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              <TextField
                                label="Link URL"
                                value={contentItem.link}
                                onChange={(e) => handleContentChange(stepIndex, contentIndex, 'link', e.target.value)}
                                fullWidth
                                placeholder="https://example.com"
                              />
                            </Box>
                          ) : (
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => handleToggleLink(stepIndex, contentIndex)}
                              variant="outlined"
                            >
                              Add Link URL
                            </Button>
                          )}
                        </Box>

                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              Sub-steps
                            </Typography>
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => handleAddSubStep(stepIndex, contentIndex)}
                              variant="outlined"
                            >
                              Add Sub-step
                            </Button>
                          </Box>
                          {contentItem.subSteps && contentItem.subSteps.length > 0 ? (
                            <Box>
                              {contentItem.subSteps.map((subStep, subIndex) => (
                                <Box key={subIndex} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                                  <TextField
                                    value={subStep}
                                    onChange={(e) => {
                                      const updatedSteps = [...steps];
                                      const updatedContent = [...updatedSteps[stepIndex].content];
                                      const updatedSubSteps = [...(updatedContent[contentIndex].subSteps || [])];
                                      updatedSubSteps[subIndex] = e.target.value;
                                      updatedContent[contentIndex] = {
                                        ...updatedContent[contentIndex],
                                        subSteps: updatedSubSteps,
                                      };
                                      updatedSteps[stepIndex] = { ...updatedSteps[stepIndex], content: updatedContent };
                                      setSteps(updatedSteps);
                                    }}
                                    fullWidth
                                    size="small"
                                    placeholder={`Sub-step ${subIndex + 1}`}
                                    sx={{ mr: 1 }}
                                  />
                                  <Tooltip title="Remove Sub-step">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleRemoveSubStep(stepIndex, contentIndex, subIndex)}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                              No sub-steps added yet
                            </Typography>
                          )}
                        </Box>
                      </Paper>
                    ))}
                    
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => handleAddContentItem(stepIndex)}
                      variant="outlined"
                      fullWidth
                      sx={{ mt: 2 }}
                    >
                      Add Content Block
                    </Button>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}

            <Box sx={{ mt: 2 }}>
              <Button
                startIcon={<AddIcon />}
                variant="outlined"
                onClick={handleAddStep}
              >
                Add Checklist Step
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        {onClose && (
          <Button
            onClick={handleCancel}
            startIcon={<CancelIcon />}
            disabled={saving}
          >
            Close
          </Button>
        )}
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={saving || loading}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Save Changes</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            You are about to save changes to the onboarding checklist steps.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Important: A hire&apos;s current checklist step is tied to the checklist number (position), not the description.
            Reordering steps or inserting/removing steps will shift checklist numbers and can affect where existing hires
            appear in their onboarding flow. Proceed only if you intend to update the step order.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button onClick={handleConfirmSave} variant="contained" startIcon={<SaveIcon />} disabled={saving}>
            {saving ? 'Saving...' : 'Confirm Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ChecklistContentSettings;

